import { useEffect, useState } from 'react';

import { ProductCard } from "../../widgets/ProductCard/ProductCard.tsx";
import { Alternatives } from "./components/Alternatives.tsx";
import { ProductCard as ProductCardInterface } from "../../widgets/ProductCard/types/productCard.interface.ts";
import { useParams } from "react-router-dom";
import { Filters } from "../../ui/Filters/Filters.tsx";
import styles from "../PartPage/PartPage.module.css";
import { FilterSchema, FilterType } from "../../ui/Filters/types/filterSchema.interface.tsx";
import Preloader from "../../ui/Loader/Preloader.tsx";
import { StockProducts } from './components/StockProducts.tsx';
import { ChoiceCar } from './components/ChoiceCar.tsx';
import { API_URL } from '../../config.tsx';
import {useAppSelector} from "@/utils/storage/stores/store.ts";
import { RootState } from "@/utils/storage/stores/store.ts";

interface Props {
    initBrandSlug?: string | null,
    initArticle?: string | null,
}

export const SparePartsPage: React.FC<Props> = ({
    initBrandSlug = null,
    initArticle = null,
}) => {
    const currentBranchId = useAppSelector((state: RootState) => state.user.userInfo?.branchId || 1);
    const { paramBrandSlug, paramArticle } = useParams<{ paramBrandSlug: string, paramArticle: string }>();
    const brandSlug = initBrandSlug || paramBrandSlug;
    const article = initArticle || paramArticle;
    const [product, setProduct] = useState<ProductCardInterface | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingAbyss, setLoadingAbyss] = useState<boolean>(false);
    const [showAbyssTitle, setShowAbyssTitle] = useState<boolean>(false);
    const [analogs, setAnalogs] = useState<{ id: number, brandId: number }[]>([]);
    const [filterSchema, setFilterSchema] = useState<FilterSchema[]>([]);
    const [filteredBrandsIds, setFilteredBrandsIds] = useState<number[]>([]);

    const getProduct = (brand: string, articleSearch: string, branchId: number, showAnalogies: boolean) => {
        fetch(`${API_URL}/product/${brand}/${articleSearch}`, {
            method: 'GET',
            credentials: 'include',
        })
            .then(response => response.json())
            .then(data => {
                if (data.productId) {
                    setProductData(data.productId, branchId, showAnalogies);
                } else {
                    setLoading(false);
                }
            })
    };

    const setProductData = (productId: number, branchId: number, showAnalogies: boolean) => {
        fetch(`${API_URL}/product_public_info/${productId}?branchId=${branchId}&with_analogs=${Number(showAnalogies)}`, {
            method: 'GET',
            credentials: 'include',
        })
            .then(response => response.json())
            .then(data => {
                setProduct(data);
                if (data.analogs.length) {
                    getAnalogs(data.analogs);
                }
            })
            .then(() => setLoading(false));

    }

    const getAnalogs = (analogs: { id: number, brandId: number, brandName: string }[]) => {
        let checkedBrandsIds: number[] = [];
        let filterSchema: FilterSchema[] = [
            {
                type: FilterType.checkbox,
                label: 'Бренд',
                name: 'brand',
                values: []
            }
        ]

        let brandsCount: { [brandId: number]: number } = {};
        analogs.map((analog: { brandId: number }) => {
            if (brandsCount[analog.brandId]) brandsCount[analog.brandId]++;
            else brandsCount[analog.brandId] = 1;
        })
        analogs.map((analog: { brandId: number, brandName: string }) => {
            if (!checkedBrandsIds.includes(analog.brandId)) {
                filterSchema[0].values.push({ id: analog.brandId, name: analog.brandName });
                checkedBrandsIds.push(analog.brandId);
            }
        })
        filterSchema[0].values.sort(function (a, b) {
            return a.count && b.count && b.count < a.count ? -1 : 1;
        });

        setAnalogs(analogs);
        setFilterSchema(filterSchema);
    }

    /** Обновление списка аналогов */
    const abyssUpdate = async (productId: number) => {
        setLoadingAbyss(true);
        setShowAbyssTitle(true);

        await requestAbyssUpdate(productId);
    };

    /** Запрос на обновление списка аналогов */
    const requestAbyssUpdate = async (productId: number, retryCount: number = 0) => {
        const MAX_RETRY_COUNT = 5; // Максимальное количество попыток обновления

        // Если количество попыток меньше максимального, то делаем запрос
        if (retryCount < MAX_RETRY_COUNT) {
            await fetch(`${API_URL}/product_abyss_update/${productId}`, {
                method: 'POST',
                credentials: 'include',
            })
                .then(() => setShowAbyssTitle(false))
                .catch(() => requestAbyssUpdate(productId, retryCount + 1));
        }
    };

    const updateData = (branchId: number, showAnalogies: boolean) => {
        setProduct(null);
        if (brandSlug && article) {
            getProduct(brandSlug, article, branchId, showAnalogies);
        }
    }

    const changeFilteredBrandsIds = (ids: number[]) => {
        setFilteredBrandsIds(ids);
    }

    const updateBranch = (branchId: number) => {
        setLoading(true);
        updateData(branchId, true);
    }

    useEffect(() => {
        if (brandSlug && article) getProduct(brandSlug, article, currentBranchId, true);
    }, [brandSlug, article, currentBranchId]);

    if (loading) return <Preloader />;

    if (product && !loadingAbyss && !analogs.length) abyssUpdate(product.productId);

    return (
        <div className={styles.wrapper}>
            {/* Боковое меню с фильтрами */}
            <Filters
                schema={filterSchema}
                setIds={changeFilteredBrandsIds}
                onFiltersApply={() => { }}
                clearFilter={() => { }}
                showButton={false}
            />
            <div>
                {/* Выбор автомобиля */}
                {product && <ChoiceCar />}

                {/* Карточка товара */}
                {product ? <ProductCard
                    product={product}
                    currentBranchId={currentBranchId}
                    updateBranch={updateBranch}
                /> : null}

                {/* Пункты выдачи */}
                {product?.deliveryPoint?.stokItems?.length ?
                    <StockProducts items={product.deliveryPoint.stokItems}
                        productData={{
                            productId: product.productId,
                            article: product.article,
                            brand: product.brand,
                            brandId: product.brandId,
                            brandSlug: product.brandSlug,
                            name: product.name,
                            img_thumbnail: product.img_thumbnail,
                        }}
                    /> : null}

                {/* Прелоадер при загрузке аналогов */}
                {showAbyssTitle
                    ? <div className={styles.abyssTitle}>
                        <h2>Загружаем аналоги...</h2>
                    </div>
                    : null}

                {/* Список аналогов */}
                {analogs.length > 0
                    ? <Alternatives
                        analogs={analogs}
                        branchId={currentBranchId}
                        filteredBrandsIds={filteredBrandsIds}
                    />
                    : null}
            </div>
        </div>
    );
};
